import { useMutation, useQuery } from '@apollo/client';
import { Box, Heading, Icon, IconSize, Text, theme, useModalState } from '@bechtel/gds';
import { i18next, useAppUser } from '@bechtel/ht-clients-common';
import {
  CreateToolOrderInput,
  ToolCatalog,
  ToolCategory,
  ToolOrder,
  ToolOrderStatus,
  ToolRoom,
  UpdateToolOrderCatalogInput,
} from '@bechtel/ht-types';

import { produce } from 'immer';
import { sumBy } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import { ErrorBoundary, ErrorModal } from '~/components';
import { ActionContent } from '~/components/action-content';
import { ConfirmDialog } from '~/components/confirm-dialog';
import LoadingIndicator from '~/components/loading-indicator';
import CommonHeader from '~/components/molecules/common-header';
import { AddUpdateNonCatalogItem } from '~/components/non-catalog';
import { SearchField } from '~/components/search-field';
import { ToolCart } from '~/components/tool-cart';
import { ToolCatalogList } from '~/components/tool-catalog/internals';
import { ToolCategoryList } from '~/components/tool-category/list';
import { GET_TOOL_CATEGORIES } from '~/gql/tool-category';
import { CREATE_TOOL_ORDER } from '~/gql/tool-order';
import { useDebounce, useUpdateToolOrder } from '~/hooks';
import { addDaysToDate } from '~/utilities';
import { ToolOrderCatalogItem } from '../../types';
import {
  CreateReservationSideSheetStyled,
  SearchButtonListAreaStyled,
  SearchSectionButtonStyled,
  SearchSectionStyled,
  ToolCartButtonAreaStyled,
  ToolCatalogHeaderStyled,
  ToolCategoryIcon,
  ToolCountStyled,
} from '../styled';
import {
  CreateReservationData,
  CreateReservationProps,
  ToolCategoryListData,
  reservationMutationRefetchQueries,
} from '../type';
import { mapToolOrderCatalogItems } from '../util';

export const CreateReservationSideSheet: React.FC<CreateReservationProps> = ({
  draftToolOrder,
  onClose,
  visible,
  toolRoomId,
}) => {
  const [toolOrder, setToolOrder] = useState<ToolOrder | undefined>(draftToolOrder);
  const [showToolCart, setShowToolCart] = useState(true);
  const [toolFavoriteCount, setToolFavoriteCount] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const {
    data: categoryListData,
    loading: categoryListLoading,
    error: categoryListError,
    refetch: categoryListRefetch,
    networkStatus: categoryListNetworkStatus,
  } = useQuery<ToolCategoryListData>(GET_TOOL_CATEGORIES);
  const [toolOrderCatalogItems, setToolOrderCatalogItems] = useState<ToolOrderCatalogItem[]>(
    mapToolOrderCatalogItems(toolOrder)
  );
  const [searchValue, setSearchValue] = useState('');
  const [selectedNonCatalogItem, setSelectedNonCatalogItem] = useState<ToolOrderCatalogItem | null>();

  const [consumable, setConsumable] = useState(false);
  const [tools, setTools] = useState(false);
  const [filterByFavorites, setFilterByFavorites] = useState(false);
  const [createDraftOrderMutation, { loading, error }] = useMutation<
    CreateReservationData,
    { createToolOrderInput: CreateToolOrderInput }
  >(CREATE_TOOL_ORDER, {
    refetchQueries: reservationMutationRefetchQueries,
  });
  const [selectedToolRoomId, setSelectedToolRoomId] = useState(toolRoomId);
  const { appUser } = useAppUser();
  const {
    open: openSaveChangesConfirmDialog,
    close: closeSaveChangesConfirmDialog,
    visible: visibleSaveChangesConfirmDialog,
  } = useModalState(false);
  const {
    updateToolOrder: updateToolOrderMutation,
    data: updatedToolOrder,
    loading: toolOrderUpdating,
    error: toolOrderUpdateError,
  } = useUpdateToolOrder();

  const [selectedToolCategory, setToolCategory] = useState<ToolCategory>({
    id: 'All',
    name: 'All Tools',
    isActive: true,
    toolHoundId: 'All',
    toolTypeId: 'All',
    toolCategoryStats: { consumablesCount: 0, toolsCount: 0 },
  });
  const [isNonCatalog, setIsNonCatalog] = useState<boolean>(false);

  const toolCategories = categoryListData?.toolCategoryGroupByName;
  const navigate = useNavigate();

  const hasCartChanges = useMemo(() => {
    if (!toolOrder) return true;
    if (
      toolOrder?.toolOrderCatalogs?.length !== toolOrderCatalogItems.length ||
      sumBy(toolOrderCatalogItems, (catalogItem) => catalogItem.quantity) !==
        sumBy(toolOrder.toolOrderCatalogs, (toolOrderCatalog) => toolOrderCatalog.quantity)
    )
      return true;
    for (const cartItem of toolOrderCatalogItems) {
      const toolOrderCatalog = toolOrder.toolOrderCatalogs.find(
        (item) =>
          item.toolCatalogId === cartItem?.toolCatalog?.id || (!item.toolCatalogId && item.nonCatalogDescription)
      );
      if (
        toolOrderCatalog?.quantity !== cartItem.quantity ||
        toolOrderCatalog.nonCatalogDescription != cartItem.nonCatalogDescription ||
        toolOrderCatalog.nonCatalogReferenceUrl != cartItem.nonCatalogReferenceUrl ||
        toolOrderCatalog.nonCatalogUomId != cartItem.nonCatalogUomId ||
        toolOrderCatalog?.similarToolCatalog?.id != cartItem.similarToolCatalog?.id
      ) {
        return true;
      }
    }
    if (selectedToolRoomId !== toolOrder.toolRoomId) return true;
    return false;
  }, [toolOrder, toolOrderCatalogItems, selectedToolRoomId]);

  const onToolCatalogItemEdit = useCallback((toolCatalog?: ToolCatalog, quantity?: number) => {
    setToolOrderCatalogItems(
      produce((draft) => {
        const index = draft.findIndex((item) => item?.toolCatalog?.id === toolCatalog?.id);
        if (index === -1) {
          draft.push({ toolCatalog, quantity: quantity || 0, id: uuidv4() });
        } else {
          if (quantity || quantity == 0) draft[index].quantity = quantity;
          else draft.splice(index, 1);
        }
      })
    );
  }, []);
  const onToolNonCatalogItemEdit = useCallback((nonCatalogItem: ToolOrderCatalogItem) => {
    setToolOrderCatalogItems(
      produce((draft) => {
        const index = draft.findIndex((item) => item?.id === nonCatalogItem.id);
        if (index === -1 && nonCatalogItem.quantity > 0) {
          draft.push({ ...nonCatalogItem, id: uuidv4() });
        } else {
          if (nonCatalogItem.quantity > 0) {
            draft[index] = nonCatalogItem;
          } else {
            draft.splice(index, 1);
            setSelectedNonCatalogItem(undefined);
            setIsNonCatalog(false);
          }
        }
      })
    );

    // setSelectedNonCatalogItem(undefined);
    //setIsNonCatalog(false);
  }, []);
  const handleChange = (e) => {
    setSearchValue(e.target.value);
  };
  const onToolRoomChange = (toolRoom: ToolRoom) => {
    setSelectedToolRoomId(toolRoom.id);
    if ((draftToolOrder && toolRoom.id != draftToolOrder?.toolRoomId) || !draftToolOrder) {
      navigate(`/reservations/new/${toolRoom.id}`);
    }
  };
  const searchText = useDebounce(searchValue, 500);
  const onCategorySelected = useCallback((toolCategory: ToolCategory) => {
    setToolCategory(toolCategory);
    setIsNonCatalog(false);
  }, []);

  const onNonCatalogSelected = useCallback(() => {
    setSelectedNonCatalogItem(null);
    setIsNonCatalog(true);
  }, []);

  const removeItemFromCart = (id: string, isNonCatalog: boolean) => {
    if (toolOrderCatalogItems.filter((x) => x.toolCatalog?.id != id)) {
      setToolOrderCatalogItems(toolOrderCatalogItems.filter((x) => x?.id != id));
    }
    if (isNonCatalog && toolOrderCatalogItems.filter((x) => x.nonCatalogDescription).length == 1) {
      setSelectedNonCatalogItem(undefined);
      setIsNonCatalog(false);
    }
  };

  const onToolCartSave = useCallback(
    (showCart) => {
      if (hasCartChanges) {
        const updatedToolOrderCatalog: UpdateToolOrderCatalogInput[] = [];
        for (const item of toolOrderCatalogItems) {
          if (item.quantity) {
            if (!toolOrder?.id) {
              updatedToolOrderCatalog.push({
                toolCatalogId: item?.toolCatalog?.id,
                quantity: item.quantity,
                nonCatalogDescription: item.nonCatalogDescription,
                nonCatalogReferenceUrl: item.nonCatalogReferenceUrl
                  ? btoa(item.nonCatalogReferenceUrl)
                  : item.nonCatalogReferenceUrl,
                nonCatalogSimilarToId: item.similarToolCatalog?.id,
                nonCatalogUomId: item.nonCatalogUomId,
              });
            } else {
              const toolOrderCatalog =
                toolOrder && toolOrder.toolOrderCatalogs?.find((catalog) => catalog.id === item?.id);
              updatedToolOrderCatalog.push({
                id: toolOrderCatalog?.id,
                requesterComments: toolOrderCatalog?.requesterComments,
                toolCatalogId: item?.toolCatalog?.id,
                quantity: item.quantity,
                nonCatalogDescription: item.nonCatalogDescription,
                nonCatalogReferenceUrl: item.nonCatalogReferenceUrl
                  ? btoa(item.nonCatalogReferenceUrl)
                  : item.nonCatalogReferenceUrl,
                nonCatalogSimilarToId: item.similarToolCatalog?.id,
                nonCatalogUomId: item.nonCatalogUomId,
              });
            }
          }
        }
        if (!updatedToolOrderCatalog.length) return;

        if (!toolOrder?.id) {
          createDraftOrderMutation({
            variables: {
              createToolOrderInput: {
                toolRoomId: toolRoomId ?? '',
                status: ToolOrderStatus.DRAFT,
                orderPlacedFor: appUser?.lobEmployeeId ?? appUser?.bun ?? '',
                recipientId: appUser?.employee?.id || '',
                requiredStartDate: addDaysToDate(new Date(), 3, true), //TODO: Required start date is not mandatory for draft orders
                toolOrderCatalog: updatedToolOrderCatalog,
              },
            },
            onCompleted: (data) => {
              closeSaveChangesConfirmDialog();
              if (onClose) {
                onClose();
              }
              if (data?.createToolOrder?.toolOrder) {
                setToolOrder(data.createToolOrder?.toolOrder);
                if (showCart) {
                  navigate(`/reservations/toolorder/${toolRoomId}`, {
                    state: { toolOrder: data.createToolOrder?.toolOrder },
                  });
                }
              }
            },
            onError: (error) => {
              setErrorMessage(i18next.t(error.message));
            },
          });
        } else {
          updateToolOrderMutation(
            {
              id: toolOrder.id,
              toolRoomId: toolRoomId ?? '',
              status: ToolOrderStatus.DRAFT,
              orderPlacedFor: appUser?.employee?.lobEmployeeId ?? appUser?.bun ?? '',
              projectId: appUser?.defaultProjectId ?? '',
              requiredStartDate: new Date(), //TODO: Required start date is not mandatory for draft orders
              toolOrderCatalog: updatedToolOrderCatalog,
              recipientId: appUser?.employee?.id || '',
            },
            (data) => {
              // setToolOrder(data?.updateToolOrder?.toolOrder);
              closeSaveChangesConfirmDialog();
              if (onClose) {
                onClose();
              }
              setToolOrderCatalogItems(mapToolOrderCatalogItems(data?.updateToolOrder?.toolOrder));
              if (showCart) {
                navigate(`/reservations/toolorder/${toolRoomId}`, {
                  state: { toolOrder: data?.updateToolOrder?.toolOrder },
                });
              }
            },
            (error) => {
              setErrorMessage(i18next.t(error.message));
            },
            reservationMutationRefetchQueries,
          );
        }
      }
    },
    [
      hasCartChanges,
      createDraftOrderMutation,
      toolRoomId,
      appUser?.employee?.lobEmployeeId,
      appUser?.bun,
      appUser?.defaultProjectId,
      toolOrderCatalogItems,
      navigate,
      updateToolOrderMutation,
      toolOrder,
      appUser?.lobEmployeeId,
    ]
  );

  const total = toolOrderCatalogItems?.reduce((acc, item) => acc + item.quantity, 0);

  if (categoryListLoading) {
    return <LoadingIndicator></LoadingIndicator>;
  }

  const onHeaderClose = () => {
    if (hasCartChanges && toolOrderCatalogItems?.length) {
      openSaveChangesConfirmDialog();
    } else if (onClose) onClose();
  };
  return (
    <ErrorBoundary>
      <CreateReservationSideSheetStyled onDismiss={close} visible={visible}>
        <>
          <CommonHeader
            leftHeadering="Add New Reservation"
            selectedToolRoomId={selectedToolRoomId}
            onToolRoomChange={onToolRoomChange}
            onClose={onHeaderClose}
            showAllToolRooms={true}
          />

          <Box>
            <div style={{ flex: '1' }}>
              <SearchSectionStyled>
                <Box>
                  <Box
                    justifyContent={{ sm: 'space-between' }}
                    px={{ sm: '500' }}
                    py={{ sm: '400' }}
                    flexGrow={{ sm: '1' }}
                    alignContent={{ sm: 'center' }}
                  >
                    <Box flexBasis={{ sm: '50%' }} mr={{ sm: '500' }}>
                      <SearchField
                        placeholder="Search Name, Catalog or Barcode..."
                        onChange={handleChange}
                      ></SearchField>
                    </Box>
                    <SearchButtonListAreaStyled>
                      <SearchSectionButtonStyled
                        onClick={() => {
                          setTools(false);
                          setConsumable(false);
                          setFilterByFavorites(!filterByFavorites);
                        }}
                        style={{ backgroundColor: filterByFavorites ? `${theme.colors.blue[100]}` : '' }}
                      >
                        <ActionContent
                          icon="star"
                          iconStyle={toolFavoriteCount > 0 ? 'solid' : 'duo'}
                          fillColor={toolFavoriteCount > 0 ? 'blue.800' : 'transparent'}
                          label="Favorites"
                          labelSize="500"
                        ></ActionContent>
                      </SearchSectionButtonStyled>
                      <SearchSectionButtonStyled
                        onClick={() => {
                          setTools(false);
                          setFilterByFavorites(false);
                          setConsumable(!consumable);
                        }}
                        style={{ backgroundColor: consumable ? `${theme.colors.blue[100]}` : '' }}
                      >
                        Non-Serialized
                      </SearchSectionButtonStyled>
                      <SearchSectionButtonStyled
                        onClick={() => {
                          setConsumable(false);
                          setFilterByFavorites(false);
                          setTools(!tools);
                        }}
                        style={{ backgroundColor: tools ? `${theme.colors.blue[100]}` : '' }}
                      >
                        Serialized
                      </SearchSectionButtonStyled>
                    </SearchButtonListAreaStyled>
                  </Box>
                  {!showToolCart && (
                    <ToolCartButtonAreaStyled>
                      <Box py={{ sm: '400' }} px={{ sm: '500' }}>
                        <SearchSectionButtonStyled onClick={() => setShowToolCart(!showToolCart)}>
                          <Icon icon="cart-shopping" fillColor="blue.800" size={IconSize.LARGE} />
                        </SearchSectionButtonStyled>
                      </Box>
                      <ToolCountStyled>
                        <Text color="white" size="200">
                          {total}
                        </Text>
                      </ToolCountStyled>
                    </ToolCartButtonAreaStyled>
                  )}
                </Box>
              </SearchSectionStyled>
              <Box>
                {toolCategories && (
                  <Box style={{ width: '263px' }} flexDirection={{ sm: 'column' }}>
                    <ToolCategoryList
                      onCategorySelected={onCategorySelected}
                      onNonToolCatalogSelected={onNonCatalogSelected}
                      toolCategories={toolCategories}
                    ></ToolCategoryList>
                  </Box>
                )}
                {!isNonCatalog && selectedToolCategory && (
                  <Box flexDirection={{ sm: 'column' }} style={{ width: '100%' }}>
                    <ToolCatalogHeaderStyled justifyContent={{ sm: 'center' }}>
                      <ToolCategoryIcon
                        className={`icon-tools-consumables icon-${selectedToolCategory.name.toLowerCase()}`}
                      ></ToolCategoryIcon>
                      <Box pt={{ sm: '400' }} pl={{ sm: '500' }}>
                        <Heading color="blue.800"> {selectedToolCategory.name} </Heading>
                      </Box>
                    </ToolCatalogHeaderStyled>
                    <ToolCatalogList
                      onToolCatalogItemEdit={(toolCatalog, quantity) => {
                        onToolCatalogItemEdit(toolCatalog, quantity);
                      }}
                      toolOrderCatalogItems={toolOrderCatalogItems}
                      toolCategory={selectedToolCategory}
                      searchInput={{
                        searchText: searchText,
                        consumables: consumable,
                        tools: tools,
                        filterByFavorites: filterByFavorites,
                      }}
                      setToolFavoriteCount={(toolFavoriteCount) => {
                        setToolFavoriteCount(toolFavoriteCount);
                      }}
                    ></ToolCatalogList>
                  </Box>
                )}
                {isNonCatalog && (
                  <AddUpdateNonCatalogItem
                    nonCatalogItem={selectedNonCatalogItem}
                    onClose={() => {
                      setIsNonCatalog(false);
                    }}
                    onAddUpdateNonCatalogItem={(values) => {
                      onToolNonCatalogItemEdit(values);
                    }}
                  ></AddUpdateNonCatalogItem>
                )}
              </Box>
            </div>
            {showToolCart && (
              <ToolCart
                toolOrderCatalogItems={toolOrderCatalogItems}
                onNonCatalogItemEdit={(cartItem, quantity, onlyQuantityEdit) => {
                  if (!onlyQuantityEdit) {
                    setSelectedNonCatalogItem(cartItem);
                    setIsNonCatalog(true);
                  } else {
                    onToolNonCatalogItemEdit({
                      ...cartItem,
                      quantity: quantity,
                      similarToolCatalog: cartItem.similarToolCatalog,
                    });
                  }
                }}
                onToolCartItemEdit={(toolCatalog, quantity) => {
                  onToolCatalogItemEdit(toolCatalog, quantity);
                }}
                onToolCartSave={(showCart) => {
                  if (hasCartChanges) {
                    onToolCartSave(showCart);
                  } else {
                    navigate(`/reservations/toolorder/${toolRoomId}`, { state: { toolOrder: toolOrder } });
                  }
                }}
                hasCartChanges={hasCartChanges}
                onToggleShowCart={() => setShowToolCart(!showToolCart)}
                onToolCartItemRemove={removeItemFromCart}
              ></ToolCart>
            )}
            {visibleSaveChangesConfirmDialog && (
              <ConfirmDialog
                message={'You have unsaved items in your cart. Do you want to save them?'}
                onConfirm={() => {
                  onToolCartSave(false);
                }}
                title="Reservation"
                onCancel={() => {
                  closeSaveChangesConfirmDialog();
                  if (onClose) {
                    onClose();
                  }
                }}
                onClose={closeSaveChangesConfirmDialog}
                cancelText="NO, CLOSE"
                confirmText="YES, SAVE ITEMS"
                loading={loading || toolOrderUpdating}
              ></ConfirmDialog>
            )}
          </Box>
          {errorMessage && (
            <ErrorModal
              message={errorMessage}
              title="Validaton Error"
              closeText="Close"
              close={() => {
                setErrorMessage('');
              }}
            />
          )}
        </>
      </CreateReservationSideSheetStyled>
    </ErrorBoundary>
  );
};
